import { Css, SelectField, Switch } from "@homebound/beam";
import { formatToPrice } from "src/components";
import { CheckoutConfigOptionGroupStep } from "src/generated/graphqlTypes";

// Minimal shape the shared helpers/components need. Both Apply and Add pages
// have GraphQL fragments that are structurally compatible with this type.
export type SharedReadyPlanOption = {
  id: string;
  name: string;
  displayName: string;
  order: number;
  active: boolean;
  optionConflicts: ReadonlyArray<{ id: string; displayName: string }>;
  optionConflictChildren: ReadonlyArray<{ id: string; displayName: string }>;
  optionPrerequisites: ReadonlyArray<{ id: string; displayName: string; optionGroup: { id: string } }>;
  optionGroup: {
    id: string;
    name: string;
    required: boolean;
    isSingleOptionGroup: boolean;
    active: boolean;
    order: number;
    step?: { code: CheckoutConfigOptionGroupStep; name: string } | null;
  };
};

export type SharedReadyPlanOptionGroup = SharedReadyPlanOption["optionGroup"] & {
  readyPlanOptions: SharedReadyPlanOption[];
};

type OptionWithGroupOrder = { id: string; displayName: string; groupOrder: number };

type SharedLineItem = { totalCostInCents: number; options: ReadonlyArray<{ id: string; displayName: string }> };

// Minimal shape for compute results that drives the per-option cost labels.
export type SharedComputeResult = {
  readyPlan: {
    optionGroups: ReadonlyArray<{
      id: string;
      order: number;
      options: ReadonlyArray<{ id: string; displayName: string }>;
    }>;
  };
  protoProjectItems: ReadonlyArray<{
    totalCostInCents: number;
    itemTemplateItem?: SharedLineItem | null;
    modifyingTemplateItems: ReadonlyArray<SharedLineItem | null | undefined>;
  }>;
};

/** Returns active options from active RPOGs. */
export function getActiveOptions<T extends SharedReadyPlanOption>(options: ReadonlyArray<T>): T[] {
  return options.filter((option) => option.active && option.optionGroup.active);
}

/** Returns inactive options or options from inactive RPOGs. */
export function getArchivedOptions<T extends SharedReadyPlanOption>(options: ReadonlyArray<T>): T[] {
  return options.filter((option) => !option.active || !option.optionGroup.active);
}

/** Creates stable RPOG models from the flat product offering option list. */
export function createOptionGroups<T extends SharedReadyPlanOption>(
  options: ReadonlyArray<T>,
): Array<T["optionGroup"] & { readyPlanOptions: T[] }> {
  const optionGroupsById = new Map<string, T["optionGroup"] & { readyPlanOptions: T[] }>();
  for (const option of options) {
    const existing = optionGroupsById.get(option.optionGroup.id);
    if (existing) {
      existing.readyPlanOptions.push(option);
    } else {
      optionGroupsById.set(option.optionGroup.id, { ...option.optionGroup, readyPlanOptions: [option] });
    }
  }

  return [...optionGroupsById.values()]
    .sortBy((optionGroup) => optionGroup.order)
    .map((optionGroup) => ({
      ...optionGroup,
      readyPlanOptions: optionGroup.readyPlanOptions.sortBy((option) => option.order ?? Number.POSITIVE_INFINITY),
    }));
}

/** Groups RPOGs into form sections by checkout step. */
export function createCheckoutStepSections<G extends SharedReadyPlanOptionGroup>(optionGroups: ReadonlyArray<G>) {
  const sectionsByStep = new Map<string, { title: string; optionGroups: G[]; sortOrder: number }>();
  for (const optionGroup of optionGroups) {
    const { code: stepCode, title } = getCheckoutStepSection(optionGroup);
    const section = sectionsByStep.get(stepCode) ?? { title, optionGroups: [], sortOrder: optionGroup.order };
    section.optionGroups.push(optionGroup);
    sectionsByStep.set(stepCode, section);
  }

  return [...sectionsByStep.values()]
    .sortBy((section) => section.sortOrder)
    .map((section) => ({ ...section, optionGroups: section.optionGroups.sortBy((optionGroup) => optionGroup.order) }));
}

/** Returns the form section for an RPOG checkout step. */
export function getCheckoutStepSection(optionGroup: SharedReadyPlanOptionGroup) {
  const stepCode = optionGroup.step?.code;
  if (
    stepCode === CheckoutConfigOptionGroupStep.ExteriorElevation ||
    stepCode === CheckoutConfigOptionGroupStep.ExteriorDesignPackage
  ) {
    return { code: "EXTERIOR", title: "Exterior" };
  }
  return { code: stepCode ?? "OTHER", title: optionGroup.step?.name ?? "Other" };
}

/** Creates the selected RPO lookup for rendering SelectField values. */
export function createSelectedOptionIdByGroupId<T extends SharedReadyPlanOption>(
  options: ReadonlyArray<T>,
  selectedOptionIds: ReadonlyArray<string>,
): Map<string, string> {
  const selectedOptionIdSet = new Set(selectedOptionIds);
  return options
    .filter((option) => selectedOptionIdSet.has(option.id))
    .sortBy((option) => [option.optionGroup.order, option.order ?? Number.POSITIVE_INFINITY])
    .reduce((map, option) => map.set(option.optionGroup.id, option.id), new Map<string, string>());
}

/** Returns the options that should be visible in an RPOG select field. */
export function getOptionsForSelect<T extends SharedReadyPlanOption>(
  optionGroup: { readyPlanOptions: T[] },
  selectedOptionIds: ReadonlyArray<string>,
  appliedOptionIds: ReadonlyArray<string>,
): T[] {
  const visibleInactiveOptionIds = new Set([...selectedOptionIds, ...appliedOptionIds]);
  return optionGroup.readyPlanOptions.filter((option) => option.active || visibleInactiveOptionIds.has(option.id));
}

/** Adds the applied cost suffix for options in the applied config. */
export function getOptionLabel(
  option: { id: string; name: string; displayName: string },
  appliedOptionIds: ReadonlyArray<string>,
  appliedOptionCostById: Map<string, number>,
): string {
  const optionName = option.displayName || option.name;
  if (!appliedOptionIds.includes(option.id)) return optionName;
  return `${optionName} (${formatToPrice({ valueInCents: appliedOptionCostById.get(option.id) ?? 0 })})`;
}

/** Returns RPO ids that cannot currently be selected. */
export function getDisabledOptionIds<T extends SharedReadyPlanOption>(
  options: ReadonlyArray<T>,
  selectedOptionIds: ReadonlyArray<string>,
): string[] {
  const selectedOptionIdSet = new Set(selectedOptionIds);
  return options
    .filter((option) => {
      if (selectedOptionIdSet.has(option.id)) return false;
      const hasConflict = [...option.optionConflicts, ...option.optionConflictChildren].some((conflictingOption) =>
        selectedOptionIdSet.has(conflictingOption.id),
      );
      const hasPrereqs = option.optionPrerequisites
        .groupBy((prereq) => prereq.optionGroup.id)
        .toValues()
        .every((prereqsInGroup) => prereqsInGroup.some((prereq) => selectedOptionIdSet.has(prereq.id)));
      return !option.active || hasConflict || !hasPrereqs;
    })
    .map((option) => option.id);
}

/** Updates the selected option ids after one RPOG select changes. */
export function updateSelectedOptionIdsForGroup<T extends SharedReadyPlanOption>(
  allOptions: T[],
  selectedOptionIds: ReadonlyArray<string>,
  optionGroupId: string,
  optionId: string | undefined,
): string[] {
  const optionIdsInGroup = new Set(
    allOptions.filter((option) => option.optionGroup.id === optionGroupId).map((option) => option.id),
  );
  const optionById = allOptions.keyByObject((option) => option.id);
  const nextOptionIds = selectedOptionIds.filter((id) => !optionIdsInGroup.has(id));
  if (optionId) nextOptionIds.push(optionId);
  return nextOptionIds.unique().sortBy((id) => {
    const option = optionById.get(id);
    return [option?.optionGroup.order ?? Number.POSITIVE_INFINITY, option?.order ?? Number.POSITIVE_INFINITY];
  });
}

/** Returns a submit disabled message when required applied RPOGs are missing. */
export function getMissingRequiredRpos<G extends SharedReadyPlanOptionGroup>(
  optionGroups: ReadonlyArray<G>,
  appliedOptionIds: ReadonlyArray<string>,
): string | undefined {
  const appliedOptionIdSet = new Set(appliedOptionIds);
  const missingRpoGroups = optionGroups
    .filter(
      (optionGroup) =>
        optionGroup.required &&
        optionGroup.readyPlanOptions.some((option) => option.active) &&
        !optionGroup.readyPlanOptions.some((option) => appliedOptionIdSet.has(option.id)),
    )
    .map((optionGroup) => optionGroup.name);
  if (missingRpoGroups.isEmpty) return undefined;
  return `${missingRpoGroups.join(", ")} missing a required option selection`;
}

/** Creates per-selected-option costs by grouping line items by their last RPOG order option. */
export function createOptionCostById(result: SharedComputeResult): Map<string, number> {
  const optionById = createOptionById(result);
  const optionCostById = new Map<string, number>();
  for (const protoProjectItem of result.protoProjectItems) {
    const lineItems = [protoProjectItem.itemTemplateItem, ...protoProjectItem.modifyingTemplateItems].compact();
    for (const lineItem of lineItems) {
      const option = getLastOption(lineItem.options, optionById);
      if (!option) continue;
      optionCostById.set(option.id, (optionCostById.get(option.id) ?? 0) + lineItem.totalCostInCents);
    }
  }
  return optionCostById;
}

/** Creates an option lookup with RPOG order from a compute result. */
function createOptionById(result: SharedComputeResult): Map<string, OptionWithGroupOrder> {
  return result.readyPlan.optionGroups
    .flatMap((optionGroup) =>
      optionGroup.options.map(
        (option) => ({ ...option, groupOrder: optionGroup.order }) satisfies OptionWithGroupOrder,
      ),
    )
    .keyByObject((option) => option.id);
}

/** Finds the line item's last option by RPOG order. */
function getLastOption(
  options: ReadonlyArray<{ id: string; displayName: string }>,
  optionById: Map<string, OptionWithGroupOrder>,
): OptionWithGroupOrder | undefined {
  return options
    .map(
      (option) =>
        optionById.get(option.id) ?? {
          id: option.id,
          displayName: `Archived Option ${option.id}`,
          groupOrder: Number.MAX_SAFE_INTEGER,
        },
    )
    .sortBy((option) => option.groupOrder).last;
}

/** Converts option ids into the form-state list shape. */
export function toFormReadyPlanOptions(optionIds: ReadonlyArray<string>) {
  return optionIds.map((readyPlanOptionId) => ({ readyPlanOptionId }));
}

/** Sums the total cost across all proto project items in a compute result. */
export function sumComputeResultCostInCents(result: SharedComputeResult): number {
  return result.protoProjectItems.reduce((total, item) => total + item.totalCostInCents, 0);
}

type ReadyPlanOptionGroupSelectProps = {
  optionGroup: SharedReadyPlanOptionGroup;
  value: string | undefined;
  selectedOptionIds: string[];
  appliedOptionIds: string[];
  appliedOptionCostById: Map<string, number>;
  disabledOptionIds: string[];
  onSelect: (optionId: string | undefined) => void;
};

/** Renders one input for an RPOG: a Routes for single-option groups, a SelectField otherwise. */
export function ReadyPlanOptionGroupSelect(props: ReadyPlanOptionGroupSelectProps) {
  const {
    optionGroup,
    value,
    selectedOptionIds,
    appliedOptionIds,
    appliedOptionCostById,
    disabledOptionIds,
    onSelect,
  } = props;
  const options = getOptionsForSelect(optionGroup, selectedOptionIds, appliedOptionIds);
  const option = options[0];

  if (optionGroup.isSingleOptionGroup && option) {
    const selected = value === option.id;
    return (
      <div css={Css.df.fdc.gap1.$} data-testid="readyPlanOptionToggle">
        <div css={Css.xsSb.gray700.$}>{`${optionGroup.name}${optionGroup.required ? " *" : ""}`}</div>
        <Switch
          label={getOptionLabel(option, appliedOptionIds, appliedOptionCostById)}
          selected={selected}
          disabled={!selected && disabledOptionIds.includes(option.id)}
          onChange={(checked) => onSelect(checked ? option.id : undefined)}
        />
      </div>
    );
  }

  return (
    <SelectField
      data-testid="readyPlanOption"
      label={`${optionGroup.name}${optionGroup.required ? " *" : ""}`}
      value={value}
      options={options}
      getOptionValue={(option) => option.id}
      getOptionLabel={(option) => getOptionLabel(option, appliedOptionIds, appliedOptionCostById)}
      onSelect={onSelect}
      nothingSelectedText="—"
      disabledOptions={disabledOptionIds}
      autoSort={false}
    />
  );
}

/** Displays configuration errors returned by computeReadyPlanCost. */
export function ConfigurationErrorsTooltip(props: { configurationErrors: string[] }) {
  const { configurationErrors } = props;
  return (
    <ul css={Css.pl2.$}>
      {configurationErrors.map((error) => (
        <li key={error}>{error}</li>
      ))}
    </ul>
  );
}
